import { type ApiAccess, type ApiAccessInput, type ApiAccessSecret, apiAccessInput } from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CopyIcon, KeyRoundIcon, PlugZapIcon, RefreshCwIcon, ShieldAlertIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api'
import { copyText } from '@/lib/clipboard'
import { useCan } from '@/lib/session'

export const Route = createFileRoute('/_app/settings/api')({
  staticData: { crumb: 'API de integración' },
  component: ApiSettingsPage,
})

const accessQuery = queryOptions({
  queryKey: ['api-access'],
  queryFn: () => api.get<ApiAccess>('/settings/api-access'),
})

const EXAMPLE = `{
  "codigo_tipo": "FE",
  "codigo_condicion_venta": "01",
  "codigos_medios_pago": ["01"],
  "numero_control": "MI-SISTEMA-0001",
  "receptor": {
    "nombre": "Cliente S.A.",
    "identificacion": { "codigo_tipo": "02", "numero": "3101123456" },
    "correo_electronico": "cliente@correo.com",
    "ubicacion": { "codigo_provincia": "1", "codigo_canton": "01", "codigo_distrito": "01" }
  },
  "elementos": [{
    "descripcion": "Servicio de soporte",
    "codigo_cabys": "8314200000000",
    "cantidad": 1,
    "precio_unitario": 50000,
    "codigo_unidad_medida": "Sp",
    "impuestos": [{ "codigo_tipo": "01", "codigo_tarifa_iva": "08", "tarifa": 13 }]
  }]
}`

const ENDPOINTS = [
  [
    'POST',
    '/documentos',
    'Emite FE, TE, NC o ND. Responde con la clave; Hacienda se procesa en segundo plano.',
  ],
  [
    'POST',
    '/consulta',
    'Estado por numero_control, clave o numero_consecutivo (xml, recibo y pdf opcionales), o por rango de fechas.',
  ],
  ['POST', '/crear/cliente', 'Crea un cliente.'],
] as const

function ApiSettingsPage() {
  const { data, isLoading } = useQuery(accessQuery)
  const canManage = useCan('settings:manage')
  const queryClient = useQueryClient()
  const [secret, setSecret] = useState<ApiAccessSecret | null>(null)
  const baseUrl = `${window.location.origin}/api`

  const refresh = (value: ApiAccess) => queryClient.setQueryData(accessQuery.queryKey, value)
  const rotate = useMutation({
    mutationFn: () => api.post<ApiAccessSecret>('/settings/api-access/rotate'),
    onSuccess: (result) => {
      setSecret(result)
      refresh(result)
    },
  })
  const setCredentials = useMutation({
    mutationFn: (input: ApiAccessInput) => api.put<ApiAccess>('/settings/api-access/credentials', input),
    onSuccess: (result) => {
      setSecret(null)
      refresh(result)
      toast.success('Credenciales guardadas')
    },
  })
  const setConsecutive = useMutation({
    mutationFn: (clientConsecutive: boolean) =>
      api.put<ApiAccess>('/settings/api-access/settings', { clientConsecutive }),
    onSuccess: refresh,
  })
  const disable = useMutation({
    mutationFn: () => api.delete<ApiAccess>('/settings/api-access'),
    onSuccess: (result) => {
      setSecret(null)
      refresh(result)
      toast.success('API desactivada')
    },
  })

  const form = useForm<ApiAccessInput>({
    resolver: zodResolver(apiAccessInput),
    values: { username: data?.username ?? '', password: '' },
  })

  if (isLoading || !data) return <Skeleton className="h-96 rounded-xl" />

  const copy = (text: string) => copyText(text)

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <PlugZapIcon className="size-4" /> API de integración
            </CardTitle>
            <CardDescription>
              Para que otros sistemas emitan y consulten comprobantes con HTTP Basic. Es compatible con la API
              del sistema anterior: los clientes solo cambian la URL base.
            </CardDescription>
          </div>
          <Badge variant={data.enabled ? 'success' : 'neutral'}>
            {data.enabled ? 'Activa' : 'Desactivada'}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Field label="URL base">
            {(c) => (
              <div className="flex gap-2">
                <Input {...c} readOnly value={baseUrl} className="font-mono text-xs" />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Copiar"
                  onClick={() => copy(baseUrl)}
                >
                  <CopyIcon />
                </Button>
              </div>
            )}
          </Field>
          {secret && (
            <div className="grid gap-2 rounded-xl border border-warning/30 bg-warning-soft p-4 text-sm">
              <div className="flex items-center gap-2 font-medium text-warning-soft-foreground">
                <ShieldAlertIcon className="size-4" /> Guardá la contraseña ahora: no se vuelve a mostrar.
              </div>
              <div className="flex items-center gap-2 font-mono text-xs">
                <span className="text-muted-foreground">Usuario</span> {secret.username}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Copiar usuario"
                  onClick={() => copy(secret.username ?? '')}
                >
                  <CopyIcon />
                </Button>
              </div>
              <div className="flex items-center gap-2 font-mono text-xs">
                <span className="text-muted-foreground">Contraseña</span> {secret.password}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Copiar contraseña"
                  onClick={() => copy(secret.password)}
                >
                  <CopyIcon />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        {canManage && (
          <CardFooter className="flex-wrap justify-between gap-2">
            <Button
              variant="outline"
              loading={rotate.isPending}
              onClick={() =>
                data.enabled
                  ? confirm({
                      title: '¿Generar credenciales nuevas?',
                      description:
                        'Los sistemas conectados dejarán de funcionar hasta que actualicen la contraseña.',
                      confirmLabel: 'Generar',
                      onConfirm: () => rotate.mutateAsync(),
                    })
                  : rotate.mutate()
              }
            >
              <RefreshCwIcon />{' '}
              {data.enabled ? 'Generar credenciales nuevas' : 'Activar y generar credenciales'}
            </Button>
            {data.enabled && (
              <Button
                variant="ghost"
                className="text-destructive"
                loading={disable.isPending}
                onClick={() =>
                  confirm({
                    title: '¿Desactivar la API?',
                    description: 'Los sistemas conectados no podrán emitir ni consultar.',
                    confirmLabel: 'Desactivar',
                    destructive: true,
                    onConfirm: () => disable.mutateAsync(),
                  })
                }
              >
                <Trash2Icon /> Desactivar
              </Button>
            )}
          </CardFooter>
        )}
      </Card>

      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRoundIcon className="size-4" /> Usuario y contraseña propios
            </CardTitle>
            <CardDescription>
              Usá las mismas credenciales que tenían tus clientes en el sistema anterior y no tendrán que
              cambiar nada más que la URL.
            </CardDescription>
          </CardHeader>
          <form onSubmit={form.handleSubmit((v) => setCredentials.mutate(v))}>
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Usuario" error={form.formState.errors.username}>
                {(c) => <Input {...c} autoComplete="off" {...form.register('username')} />}
              </Field>
              <Field label="Contraseña" error={form.formState.errors.password}>
                {(c) => (
                  <Input {...c} type="password" autoComplete="new-password" {...form.register('password')} />
                )}
              </Field>
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" loading={setCredentials.isPending}>
                Guardar credenciales
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Numeración del cliente</CardTitle>
          <CardDescription>
            Si se activa, el sistema conectado envía su propio consecutivo, sucursal, terminal, situación,
            código de seguridad y fecha de emisión en <code>encabezado</code>; Facture respeta esos valores y
            continúa la numeración interna a partir de ellos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-3 text-sm">
            <Switch
              checked={data.clientConsecutive}
              disabled={!canManage || setConsecutive.isPending}
              onCheckedChange={(v) => setConsecutive.mutate(v)}
            />
            El cliente envía el consecutivo
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Referencia rápida</CardTitle>
          <CardDescription>
            Autenticación: <code>Authorization: Basic base64(usuario:contraseña)</code>. Las respuestas son
            siempre HTTP 200 con <code>result.tipo_respuesta</code> = <code>ok</code> o <code>error</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="divide-y divide-border rounded-xl border border-border text-sm">
            {ENDPOINTS.map(([method, path, description]) => (
              <div
                key={path}
                className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:gap-4"
              >
                <span className="font-mono text-xs sm:w-44">
                  <Badge variant="outline" className="mr-2">
                    {method}
                  </Badge>
                  {path}
                </span>
                <span className="text-muted-foreground">{description}</span>
              </div>
            ))}
          </div>
          <div className="relative">
            <pre className="max-h-80 overflow-auto rounded-xl border border-border bg-subtle p-4 font-mono text-xs leading-relaxed">
              {EXAMPLE}
            </pre>
            <Button
              type="button"
              variant="outline"
              size="xs"
              className="absolute top-3 right-3"
              onClick={() => copy(EXAMPLE)}
            >
              <CopyIcon /> Copiar
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
